import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Folder, HardDrive } from 'lucide-react';
import { FileStat } from '../../api/IFileSystem';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { t } from '../../i18n';

interface TreeNodeProps {
  file: FileStat;
  depth?: number;
}

export const TreeNode: React.FC<TreeNodeProps> = ({ file, depth = 0 }) => {
  const { fs, currentPath, setCurrentPath } = useFileManagerStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [children, setChildren] = useState<FileStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [nodePath, setNodePath] = useState(file.path);

  useEffect(() => {
    let cancelled = false;
    if (file.type === 'drive') {
      fs.resolvePath(file.path)
        .then((resolved) => {
          if (!cancelled) setNodePath(resolved);
        })
        .catch(() => {
          if (!cancelled) setNodePath(file.path);
        });
      return () => {
        cancelled = true;
      };
    }
    setNodePath(file.path);
    return () => {
      cancelled = true;
    };
  }, [file.path, file.type, fs]);

  // Auto-expand if currentPath is inside this folder
  // Optimization: This might trigger too many reads on root load. 
  // Let's keep it manual expand for now, or only expand if direct parent.
  // Implementing full sync requires parsing currentPath segments.

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExpanded) {
      setIsExpanded(false);
    } else {
      if (!hasLoaded) {
          setLoading(true);
          try {
            let targetPath = nodePath;
            if (file.type === 'drive' && nodePath === file.path) {
              targetPath = await fs.resolvePath(file.path);
              setNodePath(targetPath);
            }
            const items = await fs.readdir(targetPath);
            const dirs = items.filter((f: FileStat) => f.type === 'dir' || f.type === 'drive');
            setChildren(dirs);
            setHasLoaded(true);
          } catch (e) {
            console.error(e);
          } finally {
            setLoading(false);
          }
      }
      setIsExpanded(true);
    }
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPath(nodePath);
  };

  const isSelected = currentPath === nodePath;
  const isDrive = file.type === 'drive';

  return (
    <div>
      <div 
        className={`flex items-center py-1 pr-2 rounded cursor-pointer transition-colors select-none ${isSelected ? 'bg-white/10 text-win-accent' : 'hover:bg-white/5 text-slate-300'}`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={handleSelect}
      >
        <div 
            onClick={handleToggle} 
            className="p-0.5 mr-1 hover:bg-white/10 rounded cursor-pointer text-slate-500 hover:text-slate-200"
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </div>
        
        {isDrive ? <HardDrive size={16} className="mr-2 text-slate-400" /> : <Folder size={16} className="mr-2 text-yellow-500" />}
        <span className="text-sm truncate">{file.name}</span>
      </div>

      {isExpanded && (
        <div>
          {children.map(child => (
            <TreeNode 
              key={child.path} 
              file={child} 
              depth={depth + 1}
            />
          ))}
          {children.length === 0 && !loading && hasLoaded && (
             <div className="py-1 text-xs text-slate-500 italic" style={{ paddingLeft: `${(depth + 1) * 12 + 24}px` }}>{t('empty')}</div>
          )}
        </div>
      )}
    </div>
  );
};
